import { useState, useEffect } from "react";
import { Button, ToggleSwitch } from "flowbite-react";
import { HiPencil, HiTrash, HiBan } from "react-icons/hi";
import { MdOutlineSettingsBackupRestore } from "react-icons/md";
import { UpdateReason } from "@/sharedTypes";
import DataTable from "@/components/Table";
import {
  useUpdateUpdateReasonMutation,
  useDeleteUpdateReasonMutation,
  useRestoreUpdateReasonMutation,
  useHardDeleteUpdateReasonMutation,
} from "@/api/ticketApi/updateReason";
import { toast } from "react-toastify";
import { getErrorMessage } from "@/utils/requestUtils";
import UpdateReasonFormDialog from "./UpdateReasonFormDialog";
import ConfirmationDialog from "@/components/ConfirmationDialog";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { agentRoles } from "@/constants/agent";

import { useTranslation } from "react-i18next";

interface UpdateReasonListTableProps {
  data: UpdateReason[];
  pagination: any;
  onPageChange: (page: number) => void;
  isLoading: boolean;
  filters: any;
  onFilterChange: (filters: any) => void;
}

export default function UpdateReasonListTable({
  data,
  pagination,
  onPageChange,
  isLoading,
  filters,
  onFilterChange,
}: UpdateReasonListTableProps) {
  const { t } = useTranslation(["updateReason", "common"]);
  const user = useCurrentUser();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState<UpdateReason | undefined>();
  const [showInactive, setShowInactive] = useState(false);

  const [updateReason] = useUpdateUpdateReasonMutation();
  const [deleteReason] = useDeleteUpdateReasonMutation();
  const [restoreReason] = useRestoreUpdateReasonMutation();
  const [hardDeleteReason] = useHardDeleteUpdateReasonMutation();

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
  } | null>(null);

  const handleEdit = (reason: UpdateReason) => {
    setSelectedReason(reason);
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = async (data: UpdateReason) => {
    if (!selectedReason) return;
    try {
      const payload = {
        update_reason_id: selectedReason.update_reason_id,
        update_reason_description: data.update_reason_description,
      };
      await updateReason(payload as UpdateReason).unwrap();
      toast.success(t("updateReason:updatedSuccess"));
      setIsEditModalOpen(false);
      setSelectedReason(undefined);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleDelete = (reason: UpdateReason) => {
    setConfirmDialog({
      open: true,
      title: t("updateReason:confirmDisableTitle"),
      description: t("updateReason:confirmDisableBody"),
      onConfirm: async () => {
        try {
          await deleteReason(reason.update_reason_id).unwrap();
          toast.success(t("updateReason:disabledSuccess"));
        } catch (err) {
          toast.error(getErrorMessage(err));
        }
        setConfirmDialog(null);
      },
    });
  };

  const handleRestore = (reason: UpdateReason) => {
    setConfirmDialog({
      open: true,
      title: t("updateReason:confirmRestoreTitle"),
      description: t("updateReason:confirmRestoreBody"),
      onConfirm: async () => {
        try {
          await restoreReason(reason.update_reason_id).unwrap();
          toast.success(t("updateReason:restoredSuccess"));
        } catch (err) {
          toast.error(getErrorMessage(err));
        }
        setConfirmDialog(null);
      },
    });
  };

  const handleHardDelete = (reason: UpdateReason) => {
    setConfirmDialog({
      open: true,
      title: t("updateReason:confirmDeleteTitle"),
      description: t("updateReason:confirmDeleteBody"),
      onConfirm: async () => {
        try {
          await hardDeleteReason(reason.update_reason_id).unwrap();
          toast.success(t("updateReason:deletedSuccess"));
        } catch (err) {
          toast.error(getErrorMessage(err));
        }
        setConfirmDialog(null);
      },
    });
  };

  useEffect(() => {
    onFilterChange({ ...filters, is_active: !showInactive });
  }, [showInactive]);

  const columns = [
    { key: "update_reason_id", label: t("updateReason:ref") },
    { key: "update_reason_description", label: t("updateReason:description") },
    {
      key: "actions",
      label: t("updateReason:actions"),
      render: (row: any) => (
        <div className="flex gap-2">
           {user?.agent_role === agentRoles.company_admin && (
             <>
               {showInactive ? (
                  <>
                     <Button size="xs" color="light" onClick={() => handleRestore(row)}>
                        <MdOutlineSettingsBackupRestore className="h-4 w-4 text-green-600" />
                     </Button>
                     <Button size="xs" color="light" onClick={() => handleHardDelete(row)}>
                        <HiTrash className="h-4 w-4 text-red-600" />
                     </Button>
                  </>
               ) : (
                  <>
                    <Button size="xs" color="light" onClick={() => handleEdit(row)}>
                        <HiPencil className="h-4 w-4 text-purple-600" />
                    </Button>
                    <Button size="xs" color="light" onClick={() => handleDelete(row)}>
                        <HiBan className="h-4 w-4 text-orange-600" />
                    </Button>
                  </>
               )}
             </>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="flex justify-start mb-4">
        <ToggleSwitch
            checked={showInactive}
            label={t("updateReason:showInactive")}
            onChange={setShowInactive}
        />
      </div>

      <DataTable
        columns={columns}
        data={data}
        pagination={pagination}
        onPageChange={onPageChange}
      />

      <UpdateReasonFormDialog
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleEditSubmit}
        defaultValues={selectedReason}
        mode="edit"
      />

       {confirmDialog && (
        <ConfirmationDialog
          title={confirmDialog.title}
          body={confirmDialog.description}
          onCancel={() => setConfirmDialog(null)}
          onConfirm={confirmDialog.onConfirm}
        />
      )}
    </>
  );
}
